import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, FolderKanban, Users, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2"];

export default function Projects() {
  const { user } = useAuth();
  const isSupervisor = user?.account_role === "supervisor";
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({});
  const [scope, setScope] = useState("mine");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: COLORS[0] });

  const load = async () => {
    const params = isSupervisor ? { scope } : {};
    const { data } = await api.get("/projects", { params });
    setProjects(data);
    // Fetch counts scoped same way
    const exps = await api.get("/experiments", { params: isSupervisor ? { scope } : {} });
    const c = {};
    exps.data.forEach((e) => {
      if (e.project_id) c[e.project_id] = (c[e.project_id] || 0) + 1;
    });
    setCounts(c);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, isSupervisor]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Project name required");
    await api.post("/projects", form);
    toast.success("Project created");
    setOpen(false);
    setForm({ name: "", description: "", color: COLORS[0] });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project? Experiments will be unlinked but kept.")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Projects</div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">Projects & folders</h1>
          <p className="text-sm font-body text-zinc-500 mt-1">Group experiments under thesis, papers or long-running studies.</p>
        </div>
        <div className="flex items-center gap-3">
          {isSupervisor && (
            <Tabs value={scope} onValueChange={setScope}>
              <TabsList>
                <TabsTrigger value="mine" data-testid="projects-scope-mine">Mine</TabsTrigger>
                <TabsTrigger value="all" data-testid="projects-scope-all"><Users className="h-3.5 w-3.5 mr-1" />All members</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-project-btn"><Plus className="h-4 w-4 mr-2" />New project</Button>
          </DialogTrigger>          <DialogContent>
            <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Thesis – Chapter 4" data-testid="project-name-input" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="project-desc-input" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Color</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`h-8 w-8 rounded-md border-2 ${form.color === c ? "border-zinc-900 dark:border-white" : "border-transparent"}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-project-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md p-16 text-center">
          <FolderKanban className="h-8 w-8 mx-auto text-zinc-400 mb-3" />
          <div className="font-sans font-semibold">No projects yet</div>
          <div className="text-sm font-body text-zinc-500 mt-1">Create a project to group related experiments.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="border border-zinc-200 dark:border-zinc-800 rounded-md bg-white dark:bg-zinc-900 overflow-hidden" data-testid={`project-card-${p.id}`}>
              <div className="h-2" style={{ background: p.color }} />
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-sans font-semibold text-lg truncate">{p.name}</h3>
                    <div className="text-xs font-mono text-zinc-500 mt-1">
                      {counts[p.id] || 0} experiment{(counts[p.id] || 0) !== 1 ? "s" : ""}
                    </div>
                    {p.owner_email && p.user_id !== user?.user_id && (
                      <div className="text-[10px] font-mono uppercase tracking-widest text-blue-600 mt-1 inline-flex items-center gap-1">
                        <UserIcon className="h-3 w-3" /> by {p.owner_name || p.owner_email}
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => del(p.id)} className="hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {p.description && (
                  <p className="text-sm font-body text-zinc-600 dark:text-zinc-400 mt-3 line-clamp-2">{p.description}</p>
                )}
                <div className="mt-4">
                  <Link to={`/experiments?project_id=${p.id}`}>
                    <Button size="sm" variant="outline">View experiments →</Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
